import { type ColumnDef } from "@tanstack/react-table";
import { PhoneOff } from "lucide-react";
import { useMemo, useState } from "react";

import { AppShell } from "@/components/AppShell";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusPill } from "@/components/status-pill";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCallLogs } from "@/hooks/api/flows";
import { resolveBilingual } from "@/lib/bilingual";
import { formatTimestamp } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";
import type {
  CallLogClientInfo,
  CallLogEntry,
  CallLogStep,
  CallOutcome,
  LanguagePreference,
} from "@/types/api";

const ALL = "all";

// English-only (this page follows the rest of the Admin-only tooling —
// Staff/Categories, the script editor — that stays English per lib/i18n.ts;
// only Staff-facing screens like FlowPlayerPage are bilingual).
const REASON_LABEL: Record<string, string> = {
  CUSTOMER_DISCONNECTED: "Customer disconnected",
  LOST_SIGNAL: "Lost signal",
  OTHER: "Other",
};

/** m:ss, or just "Ns" under a minute — same idea as a call timer, not a
 * stopwatch precision readout. */
function formatDuration(startedAt: string, endedAt: string): string {
  const seconds = Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${rest}s`;
}

/** Same badge, shared by the table column and the detail sheet so the two
 * can't disagree on how an outcome reads. */
function OutcomeBadge({ outcome }: { outcome: CallOutcome }) {
  return outcome === "COMPLETED" ? (
    <StatusPill kind="success">Completed</StatusPill>
  ) : (
    <StatusPill kind="warning">Ended early</StatusPill>
  );
}

/** A step's question/answer, resolved for the viewing Admin's own
 * language preference — same fallback/dedup behavior as everywhere else
 * bilingual content is shown (FlowPlayerPage, GuideListPage), rather than
 * always stacking both languages regardless of what's actually useful to
 * this admin. */
function StepEntry({ step, languagePreference }: { step: CallLogStep; languagePreference: LanguagePreference }) {
  const question = resolveBilingual(languagePreference, { en: step.question, es: step.question_es });
  const answer = resolveBilingual(languagePreference, { en: step.answer, es: step.answer_es });
  return (
    <li className="border-l-2 border-slate-200 pl-3">
      <div className="font-medium text-slate-900">{question.primary}</div>
      {question.secondary && <div className="text-xs text-slate-500">{question.secondary}</div>}
      <div className="mt-0.5 text-slate-600">→ {answer.primary}</div>
      {answer.secondary && <div className="text-xs text-slate-500">→ {answer.secondary}</div>}
    </li>
  );
}

/** The caller's personal information, on its own — a separate section
 * from "Steps taken" (not a StepEntry among the others), since it's saved
 * to its own table server-side (CallLogClientInfo) specifically so it
 * isn't mixed into the rest of what a call log records. See backend
 * apps/flows/models.py::CallLogClientInfo. */
function ClientInfoSection({ info }: { info: CallLogClientInfo }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Client information
        {info.declined_to_share && (
          <Badge tone="warning" className="normal-case tracking-normal">
            Declined to share — placeholder used
          </Badge>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <dt className="text-xs text-slate-500">Name</dt>
          <dd className="text-slate-900">
            {[info.first_name, info.last_name].filter(Boolean).join(" ") || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Date of birth</dt>
          <dd className="text-slate-900">{info.date_of_birth || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Email</dt>
          <dd className="text-slate-900">{info.email || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Phone</dt>
          <dd className="text-slate-900">{info.phone_number || "—"}</dd>
        </div>
      </dl>
    </div>
  );
}

export default function CallLogPage() {
  const languagePreference = useAuthStore((s) => s.user?.language_preference) ?? "BOTH";
  const [outcomeFilter, setOutcomeFilter] = useState<string>(ALL);
  const [detail, setDetail] = useState<CallLogEntry | null>(null);
  const { data, isLoading, error } = useCallLogs(
    outcomeFilter === ALL ? {} : { outcome: outcomeFilter as CallOutcome }
  );

  const columns = useMemo<ColumnDef<CallLogEntry>[]>(
    () => [
      {
        accessorKey: "created_at",
        header: "When",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-slate-600">
            {formatTimestamp(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: "flow_title",
        header: "Script",
        cell: ({ row }) => (
          <span className="text-sm text-slate-900">{row.original.flow_title}</span>
        ),
      },
      {
        accessorKey: "user_display_name",
        header: "Staff",
        cell: ({ row }) =>
          row.original.user_email ? (
            <div>
              <div className="text-sm text-slate-900">
                {row.original.user_display_name || row.original.user_email}
              </div>
              <div className="text-xs text-slate-500">{row.original.user_email}</div>
            </div>
          ) : (
            <span className="text-sm text-slate-400">Deleted account</span>
          ),
      },
      {
        accessorKey: "outcome",
        header: "Outcome",
        cell: ({ row }) => <OutcomeBadge outcome={row.original.outcome} />,
      },
      {
        accessorKey: "end_reason",
        header: "Reason",
        cell: ({ row }) =>
          row.original.end_reason ? (
            <span className="text-sm text-slate-600">
              {REASON_LABEL[row.original.end_reason] ?? row.original.end_reason}
            </span>
          ) : (
            <span className="text-sm text-slate-300">—</span>
          ),
      },
      {
        id: "duration",
        header: "Duration",
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs text-slate-500">
            {formatDuration(row.original.started_at, row.original.created_at)}
          </span>
        ),
      },
      {
        id: "steps",
        header: "Steps",
        cell: ({ row }) => (
          <span className="text-sm text-slate-500">{row.original.steps.length}</span>
        ),
      },
    ],
    []
  );

  return (
    <AppShell>
      <h1 className="m-0 text-2xl font-bold text-slate-900">Call log</h1>
      <p className="mt-1 text-sm text-slate-600">
        Every run through a script, for auditing — completed normally or ended early (customer
        disconnected, lost signal, or whatever the reason was). Click a row for the full
        step-by-step path.
      </p>

      <Tabs value={outcomeFilter} onValueChange={setOutcomeFilter} className="mt-4">
        <TabsList className="h-auto flex-wrap gap-2 border-none">
          <TabsTrigger value={ALL}>All</TabsTrigger>
          <TabsTrigger value="COMPLETED">Completed</TabsTrigger>
          <TabsTrigger value="ENDED_EARLY">Ended early</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-4">
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          loading={isLoading}
          error={error ? "Couldn't load the call log." : undefined}
          pageSize={50}
          onRowClick={setDetail}
          empty={
            <EmptyState
              icon={PhoneOff}
              title="Nothing logged yet"
              description="Calls will show up here as Staff complete or end them."
            />
          }
        />
      </div>

      <Sheet open={detail !== null} onOpenChange={(next) => !next && setDetail(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{detail?.flow_title}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-6 space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <OutcomeBadge outcome={detail.outcome} />
                <span className="text-xs text-slate-500">{formatTimestamp(detail.created_at)}</span>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-xs text-slate-500">
                  {formatDuration(detail.started_at, detail.created_at)}
                </span>
              </div>

              <div className="text-sm">
                <span className="font-medium text-slate-900">Staff: </span>
                <span className="text-slate-600">
                  {detail.user_display_name || detail.user_email || "Deleted account"}
                </span>
              </div>

              {detail.end_reason && (
                <div className="rounded-md border border-warning/30 bg-warning-subtle p-3 text-sm">
                  <div className="font-medium text-warning">
                    {REASON_LABEL[detail.end_reason] ?? detail.end_reason}
                  </div>
                  {detail.end_reason_note && (
                    <p className="mt-1 text-slate-700">{detail.end_reason_note}</p>
                  )}
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Steps taken
                </div>
                {detail.steps.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">
                    No steps recorded — the call ended on the very first step.
                  </p>
                ) : (
                  <ol className="mt-2 space-y-3 text-sm">
                    {detail.steps.map((step, i) => (
                      <StepEntry key={i} step={step} languagePreference={languagePreference} />
                    ))}
                  </ol>
                )}
              </div>

              {detail.client_info && (
                <div className="border-t border-slate-200 pt-4">
                  <ClientInfoSection info={detail.client_info} />
                </div>
              )}

              <div className="border-t border-slate-200 pt-3 text-sm">
                <span className="font-medium text-slate-900">
                  {detail.outcome === "COMPLETED" ? "Ended on: " : "Left on: "}
                </span>
                <span className="text-slate-600">{detail.last_step_title}</span>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}
